/**
 * 소프트웨어 목록 — 코드 ↔ 슬러그 ↔ 표시명 매핑의 SSOT.
 *
 * URL 체계(judahsWeb/PENDING_CHANGES.md §12):
 * - 제품은 코드(`/products/kdm6`), 소프트웨어는 이름 슬러그(`/software/judahs-studio`).
 * - 슬러그는 매뉴얼에 인쇄되면 고정. 개명 시 옛 슬러그를 301 로 남긴다.
 * - 코드는 URL 에 쓰지 않는 내부 식별자 = S3 `judahs-update/{code}/` 프리픽스.
 */

export interface SoftwarePlatformType {
	/**
	 * 포인터 `platforms[].platform` 과 같은 값 — 이 키로 설치파일을 찾는다.
	 * 다운로드 링크 식별자이기도 하다 (`/software/{slug}/download/{platform}` 예약)
	 */
	id: 'mac-arm64' | 'mac-x64' | 'win-x64'
	/** 방문자의 기기와 맞춰 보는 값. 맞는 플랫폼이 Download 밴드의 큰 버튼이 된다 */
	os: 'mac' | 'win'
	arch: 'arm64' | 'x64'
	/** 큰 버튼에 들어가는 이름 — `Download for macOS` */
	osName: string
	/**
	 * 같은 OS 안에서 빌드를 가르는 이름 — `Apple silicon` · `Intel` · `64-bit`.
	 * 빌드가 나란히 설 때는 버튼에도 들어간다 — `Download for macOS (Apple silicon)`
	 */
	buildName: string
	/** 큰 버튼 아래 한 줄의 앞 문장 — 어떤 기기용인가. 빌드가 하나뿐인 OS 는 비운다 */
	buildNote?: string
	/** 목록 줄에 쓰는 전체 이름. `{osName} · {buildName}` */
	label: string
	/** 포인터를 읽기 전에 보이는 파일 형식. 읽은 뒤에는 실제 파일명의 확장자를 쓴다 */
	ext: 'dmg' | 'exe'
}

export interface SoftwareType {
	code: string
	slug: string
	name: string
	tagline: string
	description: string
	/**
	 * 설치파일 포인터. `{ version, platforms: [{ platform, file }] }` 를 기대한다.
	 * `version` 은 **앱 버전이 아니라 경로 토큰**(`r1`, `r2` …)이다 — 링크를 만드는 데만 쓰고
	 * 화면에는 내지 않는다. **버전은 어디에도 적지 않는다**: 설치한 앱이 스스로 최신 정식 버전을
	 * 받기 때문에, 적어 두면 새 빌드가 나올 때마다 고칠 일만 생긴다 (2026-09-30 사용자 지시).
	 *
	 * 브라우저가 읽으므로 CORS 가 열린 S3 주소여야 한다. `update.judahs.io`(CloudFront)는 같은
	 * 파일을 주지만 CORS 헤더를 싣지 않는다 (2026-09-30 실측).
	 */
	latestUrl: string
	/**
	 * 설치파일 위치. 파일 주소 = `{downloadBaseUrl}/{version}/{file}` — 뒤 둘은 포인터에서 온다.
	 * 파일명을 여기에 적지 않는다. 새 설치파일이 나와도 이 파일은 고치지 않는다.
	 */
	downloadBaseUrl: string
	/** Download 밴드의 첫 줄. 받을 수 있고 값이 없다는 사실을 버튼보다 먼저 적는다 */
	downloadLead: string
	/**
	 * 같은 OS 의 빌드가 나란히 설 때 그 아래에 놓는 한 줄 — 어느 쪽인지 가르는 법.
	 * 칩 종류를 끝내 알아내지 못했을 때만 보인다 (WebGL 이 꺼진 브라우저 등).
	 */
	buildHintObj: { [os: string]: string | undefined }
	platforms: SoftwarePlatformType[]
	/** 무엇을 하는 소프트웨어인가. 한 줄에 하나, 화면에서는 불릿으로 나간다 */
	features: string[]
	requirements: { label: string; value: string }[]
}

/** 포인터(JSON)를 읽는 곳 — CORS 가 열려 있다 */
const UPDATE_BUCKET_ORIGIN = 'https://judahs-update.s3.ap-northeast-2.amazonaws.com'
/** 사람이 받는 주소 — 같은 버킷 앞의 CloudFront */
const UPDATE_ORIGIN = 'https://update.judahs.io'

export const softwareList: SoftwareType[] = [
	{
		code: 'studio',
		slug: 'judahs-studio',
		name: 'Judahs Studio',
		tagline: 'Your rig, edited from a desk.',
		description:
			'Studio edits what the unit holds, updates its firmware, and gets it running again when it will not start.',
		latestUrl: `${UPDATE_BUCKET_ORIGIN}/studio/shell/latest.json`,
		downloadBaseUrl: `${UPDATE_ORIGIN}/studio/shell`,
		downloadLead: "Available now for macOS and Windows. It's free.",
		buildHintObj: {
			mac: 'Apple menu > About This Mac shows which one you have. Chip means Apple silicon, Processor means Intel.',
		},
		features: [
			'Banks, songs and setlists.',
			'What every footswitch does, on tap and on hold.',
			'The loop chain and audio routing, stored per preset.',
			'MIDI messages that carry device names in place of CC numbers.',
			'Every pedal on the board, and how it connects.',
			'Expression, TRS and wireless settings.',
			'Firmware updates for the unit and its slave processors.',
			'Repair and recovery, down to a unit that will not boot.',
			'The MIDI dictionary, with room for your own entries.',
		],
		platforms: [
			{
				id: 'mac-arm64',
				os: 'mac',
				arch: 'arm64',
				osName: 'macOS',
				buildName: 'Apple silicon',
				buildNote: 'For Macs with Apple silicon.',
				label: 'macOS · Apple silicon',
				ext: 'dmg',
			},
			{
				id: 'mac-x64',
				os: 'mac',
				arch: 'x64',
				osName: 'macOS',
				buildName: 'Intel',
				buildNote: 'For Intel Macs.',
				label: 'macOS · Intel',
				ext: 'dmg',
			},
			{
				id: 'win-x64',
				os: 'win',
				arch: 'x64',
				osName: 'Windows',
				buildName: '64-bit',
				label: 'Windows · 64-bit',
				ext: 'exe',
			},
		],
		/* 값은 쓰지 않는다. 가격을 굳이 적어 두지 않는 쪽이 우리 어법에 맞다 (PERSONA §1) */
		requirements: [
			{ label: 'macOS', value: '13 Ventura or later' },
			{ label: 'Windows', value: '10 (64-bit) or 11' },
			{ label: 'Connection', value: 'USB-C, Wi-Fi or Bluetooth' },
			{ label: 'Account', value: 'A Judahs account, the same one you use here' },
			{ label: 'Hardware', value: 'Koinos PLS 8, and the models that follow' },
		],
	},
]

export const findSoftwareBySlug = (slug: string): SoftwareType | undefined =>
	softwareList.find(aSoftware => aSoftware.slug === slug)

/** 스토어에서 받는 모바일 앱의 플랫폼 하나. 설치파일 포인터가 없으므로 `SoftwarePlatformType` 과 모양이 다르다 */
export interface MobileStoreType {
	id: 'ios' | 'android'
	/** 플랫폼 메타에 쓰는 이름 — `iOS` · `Android` */
	label: string
	/** 링크 이름 — `App Store` · `Google Play` */
	storeName: string
	/** 스토어가 열리면 붙는다. 없으면 자리만 지키는 idle 텍스트로 선다 (SITEMAP_PLAN §32.4) */
	url?: string
}

/**
 * 모바일 앱 — `/software/{slug}`. 스토어 링크로 받으므로 포인터·설치파일이 없다.
 * 상세 페이지는 `SoftwareMobile.page.tsx`, 목록 행은 `Software.page.tsx` (SITEMAP_PLAN §32).
 */
export interface MobileSoftwareType {
	code: string
	slug: string
	name: string
	tagline: string
	description: string
	/** 링크가 하나도 없을 때 Download 밴드의 첫 줄 */
	unreleasedLead: string
	/**
	 * 스토어. 출시 때 `url` 을 채우면 Download 밴드가 열린다 — 폰은 자기 플랫폼의 스토어로
	 * 가는 버튼, 데스크톱은 이 페이지 주소의 QR(폰으로 열면 그 버튼이 보인다). SITEMAP_PLAN §32.14
	 */
	stores: MobileStoreType[]
	/** 데스크톱 QR 아래 한 줄 */
	qrLead: string
	/** 편집하는 제품 — Judahs 가 만든 전부 (2026-11-05 사용자 지시). 워드마크는 페이지가 code 로 고른다 */
	worksWith: { code: string; name: string }[]
	/** What it does 의 첫 줄 — 연결하고, 고치고, 기기에 써진다 (2026-11-05 사용자 지시) */
	lead: string
	/** 연결 방법. 첫 줄 아래 큰 글자로 */
	connections: string[]
	/** 그 아래 핵심 키워드. 문장이 아니라 낱말 */
	keywords: string[]
	/** 데스크톱 Studio 와의 분담. 같은 에디터의 모바일판이라는 것을 표로 보인다 */
	studioSplit: { label: string; value: string }[]
	studioLink: { label: string; path: string }
	requirements: { label: string; value: string }[]
}

export const mobileSoftwareList: MobileSoftwareType[] = [
	{
		code: 'mobile',
		slug: 'judahs-studio-mobile',
		name: 'Judahs Studio Mobile',
		tagline: 'The editor for Judahs gear, on your phone.',
		description:
			'Edits what the unit holds, over USB, Bluetooth or Wi-Fi. Backups and repair stay on the desktop.',
		unreleasedLead: 'Not yet released.',
		stores: [
			{ id: 'ios', label: 'iOS', storeName: 'App Store' },
			{ id: 'android', label: 'Android', storeName: 'Google Play' },
		],
		qrLead: 'Scan with your phone. It opens the store for it.',
		worksWith: [
			{ code: 'pls8', name: 'Koinos PLS 8' },
			{ code: 'kdm6', name: 'Kedem 6' },
			{ code: 'kdm4', name: 'Kedem 4' },
			{ code: 'amp2', name: 'AirMIDI POG 2' },
			{ code: 'ds', name: 'Dual Switch' },
		],
		lead: 'Connects to the unit. Changes what it holds. Writes it back as you go.',
		connections: ['USB', 'Bluetooth', 'Wi-Fi'],
		keywords: [
			'Switch presets',
			'Banks, songs, setlists',
			'Loop chain',
			'Pedal manager',
			'MIDI messages',
			'Expression and TRS',
			'Global tempo and clock',
			'AirMIDI',
		],
		studioSplit: [
			{ label: 'Editing', value: 'Mobile and Studio' },
			{ label: 'Backup and restore', value: 'Studio' },
			{ label: 'Firmware', value: 'Studio' },
			{ label: 'Repair', value: 'Studio' },
		],
		studioLink: {
			label: 'Judahs Studio, for macOS and Windows',
			path: '/software/judahs-studio',
		},
		requirements: [
			{ label: 'iOS', value: '16.4 or later, on iPhone and iPad' },
			{ label: 'Android', value: 'Phones and tablets' },
			{ label: 'Connection', value: 'USB, Bluetooth or Wi-Fi' },
			{ label: 'Account', value: 'A Judahs account, the same one you use here' },
			{
				label: 'Languages',
				value:
					'English, Korean, Chinese (Simplified and Traditional), Dutch, French, German, Italian, Japanese, Polish, Portuguese, Russian, Spanish, Thai, Turkish',
			},
		],
	},
]

export const findMobileSoftwareBySlug = (slug: string): MobileSoftwareType | undefined =>
	mobileSoftwareList.find(aSoftware => aSoftware.slug === slug)
